import type { RichSection } from "./resourceGuides";

export const CAMERA_INDICATOR_DISCLOSURE =
  "Each snapshot briefly activates your device camera. The camera indicator light will turn on; "
  + "we do not disable or hide hardware indicators. That visibility is a security feature. "
  + "The light is typically on for less than about one second per capture, depending on your "
  + "operating system and camera driver.";

export const WEBCAM_IDENTITY_HEADLINE =
  "Workplace webcam identity verification add-on: timed JPEG snapshots tied to screen recording "
  + "policies so reviewers can confirm who was at the keyboard, not just what was on screen.";

export const WEBCAM_PRO_SUMMARY =
  "Fixed-interval and random-interval snapshot schedules for proportionate identity checks across "
  + "the shift. Standard quality JPEG snapshots (Low/Medium profile settings) for clear identity "
  + "verification without event-driven capture.";

export const WEBCAM_ENTERPRISE_SUMMARY =
  "Everything in Webcam Capture Pro, plus trigger-based snapshots when your DeskReplay triggers "
  + "fire and a system startup snapshot when the agent starts after sign-in. Adds Best quality "
  + "profile settings: the agent requests the highest practical image settings, but resolution and "
  + "clarity still depend on each endpoint camera (built-in vs external, driver, lighting).";

export const WEBCAM_BEST_QUALITY_FAQ =
  "Best does not mean a fixed megapixel count. A 720p laptop camera stays 720p; Best selects the "
  + "top of what that device can deliver.";

export const WEBCAM_CADENCE_MATRIX = [
  { feature: "Fixed + random interval", pro: true, enterprise: true },
  { feature: "App trigger", pro: false, enterprise: true },
  { feature: "System startup", pro: false, enterprise: true },
  { feature: "Standard quality (Low/Medium)", pro: true, enterprise: true },
  { feature: "Best quality (hardware-dependent)", pro: false, enterprise: true },
] as const;

/** Endpoint workplace camera vs facility NVR plugin (FAQ and solution copy). */
export const WEBCAM_VS_CCTV_DISTINCTION =
  "Webcam Capture uses the assigned endpoint camera (built-in laptop webcam or external USB device) for timed identity JPEG snapshots tied to screen recording policies. The CCTV+ Plugin indexes NVR and IP camera streams for premises surveillance, hot-path alerts, and cross-camera search. Webcam answers who was at the keyboard or monitoring console; CCTV+ answers what facility cameras recorded.";

/** Control room and SOC operator presence (distinct from wall-camera feeds). */
export const WEBCAM_CONTROL_ROOM_BLURB =
  "In CCTV control rooms and SOCs, optional Webcam Capture verifies the analyst or guard at the VMS workstation is physically at the monitoring station. It deters leaving the desk while feeds run unattended. It does not replace NVR wall cameras; pair it with the CCTV+ Plugin when you need both operator presence and facility camera intelligence.";

/** Hardware assignment and indicator disclosure for FAQ and highlights. */
export const WEBCAM_EXTERNAL_USB_BLURB =
  "Profiles assign which camera the agent captures from. Built-in laptop webcams work for typical desks; external USB webcams are supported when monitors block the built-in lens or the control room uses a dedicated camera on a separate mount. Each snapshot briefly activates the camera; the hardware indicator light is visible by design (typically under about one second per shot, OS-dependent). This is identity verification, not covert surveillance.";

export const WEBCAM_PRICING_ANCHOR = "/pricing#webcam-capture-addon";

export const WEBCAM_CAPTURE_GUIDE_SECTIONS: RichSection[] = [
  {
    heading: "Policy-on-match, not always-on video",
    blocks: [
      {
        type: "p",
        text: "Webcam Capture stores timed JPEG identity snapshots, not continuous webcam video. Snapshots fire only when an in-scope capture policy runs: scheduled shifts, random interval checks, app triggers (Enterprise), or system startup (Enterprise). That keeps identity checks proportionate to the same policy boundaries as screen recording.",
      },
      {
        type: "callout",
        label: "Indicator disclosure",
        text: CAMERA_INDICATOR_DISCLOSURE,
      },
    ],
  },
  {
    heading: "Pro vs Enterprise cadence",
    blocks: [
      {
        type: "table",
        headers: ["Capability", "Webcam Capture Pro", "Webcam Capture Enterprise"],
        rows: WEBCAM_CADENCE_MATRIX.map((row) => [
          row.feature,
          row.pro ? "Yes" : "No",
          row.enterprise ? "Yes" : "No",
        ]),
      },
      {
        type: "p",
        text: WEBCAM_BEST_QUALITY_FAQ,
      },
    ],
  },
  {
    heading: "Seats, enablement, and gallery vs timeline",
    blocks: [
      {
        type: "bullets",
        items: [
          "Purchase module seats on Pro or Enterprise endpoint pools, enable workplace webcam in Settings, and complete legal acknowledgement before rollout.",
          "Assign webcam profiles to capture policies and bind module seats to endpoints when seat counts are limited.",
          "Reviewers open snapshots from endpoint timelines alongside session segments, or browse the webcam gallery for identity checks across endpoints.",
          "Pair with screen replay: identity JPEGs answer who was at the keyboard; screen segments answer what happened on glass.",
        ],
      },
    ],
  },
  {
    heading: "Webcam vs CCTV+",
    blocks: [
      {
        type: "p",
        text: WEBCAM_VS_CCTV_DISTINCTION,
      },
    ],
  },
];
